// src/pages/qc/PageQCFgSpecs.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { FgSpecModal } from "./FgSpecModal";
import { useToast } from "../../components/Toast";

export function PageQCFgSpecs({ fgs, sfgs }) {
  const showToast = useToast();
  const [specs,   setSpecs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState("");
  const [hovCol,  setHovCol]  = useState(null);
  const [modal,   setModal]   = useState(null); // {fgId, spec}

  useEffect(() => {
    api.fetchFgSpecs()
      .then(setSpecs)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() =>
    specs.filter(s => s.fgId.toLowerCase().includes(search.toLowerCase())),
    [specs, search]);

  const fgsWithoutSpec = useMemo(() =>
    [...fgs, ...(sfgs||[])].sort((a, b) => a.id.localeCompare(b.id)),
  [fgs, sfgs]);

  async function handleSave(fgId, form) {
    const saved = await api.saveFgSpec(fgId, form);
    setSpecs(old => {
      const idx = old.findIndex(s => s.fgId === fgId);
      return idx >= 0
        ? old.map(s => s.fgId === fgId ? saved : s)
        : [...old, saved].sort((a,b) => a.fgId.localeCompare(b.fgId));
    });
    showToast("Spec saved");
    setModal(null);
  }

  function fgTag(fgId) {
    if (fgs.some(f => f.id === fgId))
      return <span className="badge badge-green"  style={{ marginLeft:6 }}>FG</span>;
    if ((sfgs||[]).some(f => f.id === fgId))
      return <span className="badge badge-purple" style={{ marginLeft:6 }}>SFG</span>;
    return <span className="badge badge-green" style={{ marginLeft:6 }}>FG</span>;
  }

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Water Based Specifications</div>
          <div className="page-subtitle">FG &amp; SFG quality parameters</div>
        </div>
        <div className="page-header-right">
          <button className="btn btn-primary" onClick={() => setModal({ fgId:null, spec:null })}>+ Add Spec</button>
        </div>
      </div>
      <div className="action-bar">
        <input className="form-input" placeholder="Search grade…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ width:220 }} />
      </div>

      {loading && <div style={{ color:"#aaa", fontSize:12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
        <table className="data-table" data-hov-col={hovCol ?? undefined} onMouseLeave={() => setHovCol(null)}>
          <thead><tr>
            {["Grade","Appearance","Solid Content (%)","pH","Density (g/mL)","Viscosity (cP)","Spindle / RPM","B4 Cup (s)","Temp (°C)",""].map((h,i) => (
              <th key={i} onMouseEnter={() => setHovCol(i)}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {filtered.map(s => {
              const hasB4  = s.b4CupMin != null;
              const hasBrk = s.brookfieldMin != null;
              return (
                <tr key={s.fgId}>
                  <td onMouseEnter={() => setHovCol(0)} className="fw-600">
                    {s.fgId}{fgTag(s.fgId)}
                  </td>
                  <td onMouseEnter={() => setHovCol(1)}>
                    <span className={`badge ${s.appearance==="Clear"?"badge-blue":"badge-gray"}`}>{s.appearance ?? "—"}</span>
                  </td>
                  <td onMouseEnter={() => setHovCol(2)}>{s.solidContentMin != null ? `${s.solidContentMin} – ${s.solidContentMax}` : "—"}</td>
                  <td onMouseEnter={() => setHovCol(3)}>{s.phMin != null ? `${s.phMin} – ${s.phMax}` : "—"}</td>
                  <td onMouseEnter={() => setHovCol(4)}>{s.densityMin != null ? `${s.densityMin} – ${s.densityMax}` : "—"}</td>
                  <td onMouseEnter={() => setHovCol(5)}>
                    {hasBrk ? `${s.brookfieldMin} – ${s.brookfieldMax}` : (hasB4 ? <span className="text-muted">B4 cup</span> : "—")}
                  </td>
                  <td onMouseEnter={() => setHovCol(6)}>{hasBrk ? `Sp${s.brookfieldSpindle} / ${s.brookfieldRpm} rpm` : "—"}</td>
                  <td onMouseEnter={() => setHovCol(7)}>{hasB4 ? `${s.b4CupMin} – ${s.b4CupMax}` : "—"}</td>
                  <td onMouseEnter={() => setHovCol(8)}>{s.brookfieldTempC ?? "—"}</td>
                  <td onMouseEnter={() => setHovCol(9)}>
                    <button className="btn-small" onClick={() => setModal({ fgId:s.fgId, spec:s })}>Edit</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {modal !== null && (
        <div className="overlay" onClick={e => { if (e.target===e.currentTarget) setModal(null); }}>
          <FgSpecModal
            fgId={modal.fgId} spec={modal.spec}
            fgsWithoutSpec={fgsWithoutSpec}
            onSave={handleSave}
            onCancel={() => setModal(null)}
          />
        </div>
      )}
    </>
  );
}
